import { useUserContext } from "../../providers/UserProvider";
import { useInvitations } from "../useInvitations/useInvitations";
import { useCsmOrganizations } from "../useCsmOrganizations/useCsmOrganizations";
import { ALL_PAGES_SIZE, INVITATION_REVOKE_DAYS } from "../../constants/constants";
import { Invitation } from "../../domain/Invitation";
import { IUserInstancePermission } from "../../domain/UserInstancePermission";

export interface SupportOrganizationPermissions {
    relatedInvitations: Invitation[];
    instancePermissions: IUserInstancePermission[];
    revokeDate?: Date;
    organizationId: string;
    organizationName: string;
}

/**
 * Derives the list of organizations a support engineer has access to, with the revoke date for each.
 * This is the single source of truth shared by the Support > Access tab and the Support > Developer
 * Settings tab so that both always show an identical list.
 *
 * Note: this list is intentionally NOT filtered by a search term — callers apply their own search
 * filtering and pagination on top of the returned list.
 */
export const useSupportOrganizations = () => {
    const { profile, instancePermissions } = useUserContext();
    const { hideFromSupportTableOrgs } = useCsmOrganizations();

    const {
        invitations,
        isLoading,
        createInvitation,
        mutate: refreshInvitations
    } = useInvitations({
        createdBy: profile?.email,
        inviteeEmail: profile?.email,
        pageSize: ALL_PAGES_SIZE
    });

    const csmOrgIds = hideFromSupportTableOrgs.map((o) => o.id);

    const organizationPermissions: SupportOrganizationPermissions[] =
        invitations
            ?.filter((i) => !!i.acceptedDate)
            .filter((i) => !csmOrgIds.includes(i.organizationId))
            .reduce((list: SupportOrganizationPermissions[], invitation) => {
                const existingOrgPermission = list.find((op) => op.organizationId === invitation.organizationId);

                if (existingOrgPermission) {
                    existingOrgPermission.relatedInvitations.push(invitation);
                } else {
                    list.push({
                        relatedInvitations: [invitation],
                        instancePermissions:
                            instancePermissions?.filter((ip) => ip.organizationId === invitation.organizationId) || [],
                        organizationId: invitation.organizationId,
                        organizationName: invitation.organizationName || ""
                    });
                }

                return list;
            }, [])
            .map((sop) => {
                const [mostRecentInvite] = sop.relatedInvitations
                    .filter((i) => !!i.acceptedDate)
                    .sort((a, b) => {
                        return (b.acceptedDate?.getTime() || 0) - (a.acceptedDate?.getTime() || 0);
                    });

                let revokeDate: Date | undefined = undefined;
                if (mostRecentInvite?.acceptedDate) {
                    revokeDate = new Date(
                        new Date(mostRecentInvite.acceptedDate).setDate(
                            mostRecentInvite.acceptedDate.getDate() + INVITATION_REVOKE_DAYS
                        )
                    );
                }

                return {
                    ...sop,
                    revokeDate
                };
            })
            .filter((op) => op.instancePermissions.length > 0)
            .sort((a, b) => {
                return (b?.revokeDate?.getTime() || 0) - (a?.revokeDate?.getTime() || 0);
            }) || [];

    return {
        organizationPermissions,
        isLoading,
        createInvitation,
        refreshInvitations
    };
};
